import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { FaSearch, FaStar, FaShoppingCart } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Products = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [sortBy, setSortBy] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PRODUCTS.LIST);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Category to brands mapping
  const categoryBrands = {
    "Cuban": [
      "Cohiba",
      "Trinidad",
      "Montecristo",
      "Partagas",
      "Romeo Y Julieta",
      "H.Upmann",
      "Hoyo de Monterrey",
      "Quintero",
      "Jose L.Piedra"
    ],
    "Non-Cuban": [
      "Olivia",
      "Camacho",
      "Drew Estate Liga Privada",
      "AJ Fernandez",
      "Gurkha",
      "Perdomo",
      "Alec Bradley",
      "Plasencia",
      "Foundation",
      "Davidoff",
      "E.P. Carillo",
      "Rocky Patel",
      "Montecristo",
      "Diesel",
      "La Aurora",
      "El Septimo",
      "Arturo Fuente"
    ],
    "Cigarillo": [
      "Cohiba",
      "Montecristo",
      "Partagas",
      "Romeo Y Julieta",
      "Trinidad",
      "Davidoff",
      "Toscano Italy"
    ]
  };

  const categories = Object.keys(categoryBrands);
  const brands = selectedCategory ? categoryBrands[selectedCategory] : Array.from(new Set(Object.values(categoryBrands).flat()));
  const priceRanges = [
    { value: 'all', label: t('products.allPrices') },
    { value: '0-200', label: `Under EGP 200` },
    { value: '200-500', label: `EGP 200 - EGP 500` },
    { value: '500-1000', label: `EGP 500 - EGP 1,000` },
    { value: '1000-2000', label: `EGP 1,000 - EGP 2,000` },
    { value: '2000+', label: `Over EGP 2,000` }
  ];
  const sortOptions = [
    { value: '', label: t('common.default') },
    { value: 'name-asc', label: t('common.nameAZ') },
    { value: 'name-desc', label: t('common.nameZA') },
    { value: 'price-asc', label: t('common.priceLowHigh') },
    { value: 'price-desc', label: t('common.priceHighLow') }
  ];

  // Filtering
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesBrand = !selectedBrand || product.brand === selectedBrand;
    let matchesPrice = true;
    if (priceRange !== 'all') {
      if (priceRange === '0-200') matchesPrice = product.price_single < 200;
      else if (priceRange === '200-500') matchesPrice = product.price_single >= 200 && product.price_single < 500;
      else if (priceRange === '500-1000') matchesPrice = product.price_single >= 500 && product.price_single < 1000;
      else if (priceRange === '1000-2000') matchesPrice = product.price_single >= 1000 && product.price_single < 2000;
      else if (priceRange === '2000+') matchesPrice = product.price_single >= 2000;
    }
    return matchesSearch && matchesCategory && matchesBrand && matchesPrice;
  });
  // Sorting
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.product_name.localeCompare(b.product_name);
      case 'name-desc':
        return b.product_name.localeCompare(a.product_name);
      case 'price-asc':
        return (a.price_single || 0) - (b.price_single || 0);
      case 'price-desc':
        return (b.price_single || 0) - (a.price_single || 0);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-cigar-cream flex items-center justify-center">
        <div className="text-2xl text-cigar-dark">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cigar-cream py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-cigar font-bold text-cigar-dark mb-3 sm:mb-4">
            {t('products.title')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            {t('products.subtitle')}
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <FaSearch className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400`} />
              <input
                type="text"
                placeholder={t('products.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`input-field ${isRTL ? 'pr-10' : 'pl-10'} text-sm sm:text-base`}
              />
            </div>
            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={e => {
                setSelectedCategory(e.target.value);
                setSelectedBrand("");
              }}
              className="input-field text-sm sm:text-base"
            >
              <option value="">{t('products.allCategories')}</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            {/* Brand Filter */}
            <select
              value={selectedBrand}
              onChange={e => setSelectedBrand(e.target.value)}
              className="input-field text-sm sm:text-base"
              disabled={!brands.length}
            >
              <option value="">{t('products.allBrands')}</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            {/* Price Range Filter */}
            <select
              value={priceRange}
              onChange={e => setPriceRange(e.target.value)}
              className="input-field text-sm sm:text-base"
            >
              {priceRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="input-field text-sm sm:text-base"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {/* Clear Filters Button */}
          <div className="mt-4 flex justify-center sm:justify-end">
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("");
                setSelectedBrand("");
                setPriceRange("all");
                setSortBy("");
              }}
              className="btn-secondary text-sm sm:text-base px-4 py-2"
            >
              {t('products.clearFilters')}
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 sm:mb-6">
          <p className="text-gray-600 text-sm sm:text-base">
            {t('products.showingResults', { count: sortedProducts.length, total: products.length })}
          </p>
        </div>

        {/* Products Grid */}
        {sortedProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {sortedProducts.map((product) => (
              <div key={product._id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="w-full h-72 flex justify-center items-center">
                  <img
                    src={(product.images && product.images[0]) || product.image || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=600&fit=crop'}
                    alt={product.product_name}
                    className="w-full h-full object-cover m-0 p-0 border-0 rounded-none"
                  />
                </div>
                
                <div className="p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-semibold text-cigar-dark mb-2 line-clamp-2">
                    {product.product_name}
                  </h3>
                  
                  <div className="flex items-center mb-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <FaStar 
                          key={i} 
                          className={`w-3 h-3 sm:w-4 sm:h-4 ${i < (product.rating || 4) ? 'text-cigar-gold' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                    <span className="ml-2 text-xs sm:text-sm text-gray-600">
                      ({product.rating || 4}.0)
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-xs sm:text-sm mb-3 line-clamp-2">
                    {product.description || 'Premium handcrafted cigar with exceptional flavor and aroma.'}
                  </p>
                  
                  <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                    {product.price_single > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">{t('products.single')}:</span>
                        <span className="text-sm sm:text-lg font-bold text-cigar-dark">
                          {product.currency} {product.price_single}
                        </span>
                      </div>
                    )}
                    {product.price_box > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">{t('products.box')} ({product.box_size}):</span>
                        <span className="text-sm sm:text-lg font-bold text-cigar-bronze">
                          {product.currency} {product.price_box}
                        </span>
                      </div>
                    )}
                    {product.price_packet > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">{t('products.packet')}:</span>
                        <span className="text-sm sm:text-lg font-bold text-cigar-gold">
                          {product.currency} {product.price_packet}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/products/${product._id}`}
                      className="flex-1 bg-cigar-brown hover:bg-cigar-dark text-white text-center py-2 rounded transition-colors duration-200 text-sm sm:text-base"
                    >
                      {t('products.viewDetails')}
                    </Link>
                    <button className="bg-cigar-gold hover:bg-cigar-light text-cigar-dark p-2 rounded transition-colors duration-200">
                      <FaShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl text-gray-300 mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('products.noCigarsFound')}</h3>
            <p className="text-gray-500">
              {t('products.noCigarsDesc')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
